/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { t } from '@sidekick/copy';
import { appPalette } from '@sidekick/design-tokens';
import { useState, type ReactNode } from 'react';
import { useMediaQuery } from '../lib/useMediaQuery.js';
import { SecondGlyph } from './SecondGlyph.js';
import { Wordmark } from './Wordmark.js';

/**
 * App shell ported from ABAppShell artboard. On desktop, a left-rail nav
 * lists every surface; on mobile, a fixed bottom tab bar exposes the
 * primary surfaces with a "More" sheet for the rest. Main canvas slot
 * holds child route content. Clicking an item calls `onModeChange`.
 */

const p = appPalette;

const sidebarStyle: React.CSSProperties = {
  background: p.bgAlt,
  borderRight: `1px solid ${p.line}`,
  padding: '22px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 28,
  width: 220,
  flexShrink: 0,
};

const monoLabel: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: p.muted,
};

export type NavMode =
  | 'letter'
  | 'docket'
  | 'constitution'
  | 'weekly'
  | 'quarterly'
  | 'record'
  | 'counsel'
  | 'account';

const modes: ReadonlyArray<{ key: NavMode; name: string; group: 'primary' | 'more' }> = [
  { key: 'letter', name: t.nav.letter, group: 'primary' },
  { key: 'docket', name: t.nav.docket, group: 'primary' },
  { key: 'constitution', name: t.nav.constitution, group: 'primary' },
  { key: 'weekly', name: t.nav.weeklyReview, group: 'more' },
  { key: 'quarterly', name: t.nav.quarterlyReview, group: 'more' },
  { key: 'record', name: t.nav.record, group: 'more' },
  { key: 'counsel', name: t.nav.counsel, group: 'primary' },
  { key: 'account', name: t.nav.account, group: 'more' },
];

// Rail order: Letter, Docket, Constitution, Weekly Review, Quarterly
// Review, The Record, Counsel, Account.
const railOrder: ReadonlyArray<NavMode> = [
  'letter',
  'docket',
  'constitution',
  'weekly',
  'quarterly',
  'record',
  'counsel',
  'account',
];

const primaryModes = modes.filter((m) => m.group === 'primary');
const moreModes = modes.filter((m) => m.group === 'more');

function nameOf(key: NavMode): string {
  return modes.find((m) => m.key === key)?.name ?? key;
}

export interface AppShellProps {
  children: ReactNode;
  currentMode: NavMode;
  onModeChange: (mode: NavMode) => void;
  onSignOut?: () => void;
  signedInAs?: string;
}

export function AppShell(props: AppShellProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  return isMobile ? <MobileShell {...props} /> : <RailShell {...props} />;
}

function RailShell({ children, currentMode, onModeChange, onSignOut, signedInAs }: AppShellProps) {
  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        background: p.bg,
        color: p.fg,
      }}
    >
      <aside style={sidebarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SecondGlyph size={26} />
          <Wordmark size={18} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ ...monoLabel, marginBottom: 6 }}>Today</div>
          {railOrder.map((key) => {
            const isCurrent = key === currentMode;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onModeChange(key)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  padding: '10px 12px',
                  margin: '0 -12px',
                  background: isCurrent ? p.bg : 'transparent',
                  borderLeft: isCurrent ? `2px solid ${p.accent}` : '2px solid transparent',
                  border: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  borderBottom: 'none',
                  fontSize: 14,
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'inherit',
                  font: 'inherit',
                }}
              >
                <span style={{ color: p.fg }}>{nameOf(key)}</span>
              </button>
            );
          })}
        </div>

        <div
          style={{
            marginTop: 'auto',
            borderTop: `1px solid ${p.line}`,
            paddingTop: 14,
            fontSize: 12,
            color: p.muted,
          }}
        >
          <div>Your Second is in.</div>
          {signedInAs ? <div style={{ marginTop: 6 }}>{signedInAs}</div> : null}
          {onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              style={{
                ...monoLabel,
                marginTop: 10,
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              {t.auth.signOut} →
            </button>
          ) : null}
        </div>
      </aside>

      <main style={{ flex: 1, overflow: 'auto' }}>{children}</main>
    </div>
  );
}

const TAB_BAR_HEIGHT = 56;

function MobileShell({ children, currentMode, onModeChange, onSignOut }: AppShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = moreModes.some((m) => m.key === currentMode);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: p.bg,
        color: p.fg,
      }}
    >
      <main style={{ flex: 1, overflow: 'auto', paddingBottom: TAB_BAR_HEIGHT }}>{children}</main>

      {moreOpen ? (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: TAB_BAR_HEIGHT,
            background: p.bgAlt,
            borderTop: `1px solid ${p.line}`,
            display: 'flex',
            flexDirection: 'column',
            padding: '8px 0',
            zIndex: 2,
          }}
        >
          {moreModes.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => {
                onModeChange(m.key);
                setMoreOpen(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '14px 22px',
                textAlign: 'left',
                fontSize: 15,
                color: m.key === currentMode ? p.accent : p.fg,
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              {m.name}
            </button>
          ))}
          {onSignOut ? (
            <button
              type="button"
              onClick={() => {
                setMoreOpen(false);
                onSignOut();
              }}
              style={{
                ...monoLabel,
                background: 'transparent',
                border: 'none',
                padding: '14px 22px',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              {t.auth.signOut} →
            </button>
          ) : null}
        </div>
      ) : null}

      <nav
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          height: TAB_BAR_HEIGHT,
          background: p.bgAlt,
          borderTop: `1px solid ${p.line}`,
          display: 'flex',
          alignItems: 'stretch',
          zIndex: 3,
        }}
      >
        {primaryModes.map((m) => (
          <TabButton
            key={m.key}
            label={m.name}
            active={m.key === currentMode}
            onClick={() => {
              setMoreOpen(false);
              onModeChange(m.key);
            }}
          />
        ))}
        <TabButton
          label="More"
          active={moreOpen || moreActive}
          onClick={() => setMoreOpen((open) => !open)}
        />
      </nav>
    </div>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        background: 'transparent',
        border: 'none',
        borderTop: active ? `2px solid ${p.accent}` : '2px solid transparent',
        color: active ? p.fg : p.muted,
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        padding: '0 4px',
      }}
    >
      {label}
    </button>
  );
}
